import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "../containers/Dialog.tsx";
import { Button } from "../widgets/Button.tsx";
import { TextInput } from "../inputs/TextInput.tsx";
import {
  useCreateWorkoutRoutine,
  usePatchWorkoutRoutine,
  useCreateExercise,
} from "../../api/hooks.ts";
import {
  CreateWorkoutRoutineRequest,
  PatchWorkoutRoutineRequest,
  WorkoutRoutine,
  Exercise,
  ExerciseReference,
} from "../../api/types.ts";
import { isNotNullish } from "../../util/typeGuards.ts";
import { useDialog } from "../DialogManager.tsx";
import { RoutineExerciseList } from "../routines/RoutineExerciseList.tsx";
import { FetchedExercise } from "../../util/exercise.tsx";

interface RoutineValidationErrors {
  name?: string;
}

interface RoutineDialogProps {
  open: boolean;
  closeDialog: () => void;
  routine?: WorkoutRoutine;
  exercises: Exercise[];
}

const RoutineDialog: React.FC<RoutineDialogProps> = ({
  open,
  closeDialog,
  routine,
  exercises,
}) => {
  const [routineName, setRoutineName] = useState<string | undefined>(undefined);
  const [validationErrors, setValidationErrors] =
    useState<RoutineValidationErrors>({});
  const [routineExercises, setRoutineExercises] = useState<ExerciseReference[]>(
    [],
  );

  const createRoutineMutation = useCreateWorkoutRoutine();
  const patchRoutineMutation = usePatchWorkoutRoutine();

  const availableExercises = useMemo(() => {
    const available = new Map<string, FetchedExercise>();

    exercises.forEach((ex) =>
      available.set(ex.id, { exercise: ex, external: false }),
    );

    return available;
  }, [exercises]);

  const createExerciseMutation = useCreateExercise();

  const { setError, openDialog } = useDialog();

  // Update routineName when routine prop changes
  useEffect(() => {
    if (isNotNullish(routine)) {
      setRoutineName(routine.name);
      setRoutineExercises(routine.exercises);
    } else {
      setRoutineName(undefined);
      setRoutineExercises([]);
    }
  }, [routine]);

  useEffect(() => {
    if (
      createExerciseMutation.isError ||
      createRoutineMutation.isError ||
      patchRoutineMutation.isError
    ) {
      console.error("Error:", {
        createExercise: createExerciseMutation.error,
        createRoutine: createRoutineMutation.error,
        patchRoutine: patchRoutineMutation.error,
      });
    }
  }, [
    createExerciseMutation.isError,
    createRoutineMutation.isError,
    patchRoutineMutation.isError,
    createExerciseMutation.error,
    createRoutineMutation.error,
    patchRoutineMutation.error,
  ]);

  const validateForm = (): boolean => {
    const errors: RoutineValidationErrors = {};

    if (!routineName || routineName.trim().length === 0) {
      errors.name = "Routine name is required";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const routineData:
      | CreateWorkoutRoutineRequest
      | PatchWorkoutRoutineRequest = {
      name: routineName!,
      exercises: routineExercises,
    };

    if (routine) {
      // Edit mode
      patchRoutineMutation.mutate(
        { id: routine.id, routine: routineData },
        {
          onSuccess: () => {
            closeDialog();
          },
          onError: (error) => {
            setError(error as Error);
          },
        },
      );
    } else {
      // Create mode
      createRoutineMutation.mutate(routineData as CreateWorkoutRoutineRequest, {
        onSuccess: () => {
          closeDialog();
        },
        onError: (error) => {
          setError(error as Error);
        },
      });
    }
  };

  const handleCancel = () => {
    setRoutineName(undefined);
    setRoutineExercises([]);
    setValidationErrors({});
    closeDialog();
  };

  const handleAddRoutineExercise = (exerciseId: string) => {
    const fetched = availableExercises.get(exerciseId);

    if (!fetched?.exercise) {
      console.error("Exercise not found:", exerciseId);
      return;
    }

    setRoutineExercises([
      ...routineExercises,
      {
        exercise_id: exerciseId,
        sets: [],
        resistance_unit: fetched.exercise.default_resistance_unit,
      },
    ]);
  };

  const handleDeleteRoutineExercise = (index: number) => {
    setRoutineExercises(routineExercises.filter((_, i) => i !== index));
  };

  const handleRoutineExerciseChange = (
    index: number,
    newExerciseId: string,
    newUnit: string,
  ) => {
    const newRoutineExercises = [...routineExercises];
    newRoutineExercises[index] = {
      ...newRoutineExercises[index],
      exercise_id: newExerciseId,
      resistance_unit: newUnit as "kg" | "lbs",
    };
    setRoutineExercises(newRoutineExercises);
  };

  const handleAddSet = (
    routineExerciseIndex: number,
    repetitions: number,
    resistance: number,
  ) => {
    const newRoutineExercises = [...routineExercises];
    newRoutineExercises[routineExerciseIndex] = {
      ...newRoutineExercises[routineExerciseIndex],
      sets: [
        ...newRoutineExercises[routineExerciseIndex].sets,
        { repetitions, resistance },
      ],
    };
    setRoutineExercises(newRoutineExercises);
  };

  const handleEditSet = (
    routineExerciseIndex: number,
    setIndex: number,
    repetitions: number,
    resistance: number,
  ) => {
    const newRoutineExercises = [...routineExercises];
    // Delete set if both repetitions and resistance are zero
    if (repetitions === 0 && resistance === 0) {
      newRoutineExercises[routineExerciseIndex] = {
        ...newRoutineExercises[routineExerciseIndex],
        sets: newRoutineExercises[routineExerciseIndex].sets.filter(
          (_, idx) => idx !== setIndex,
        ),
      };
    } else {
      // Otherwise, update the set
      const updatedSets = newRoutineExercises[routineExerciseIndex].sets.map(
        (set, idx) => {
          if (idx === setIndex) {
            return { repetitions, resistance };
          }
          return set;
        },
      );
      newRoutineExercises[routineExerciseIndex] = {
        ...newRoutineExercises[routineExerciseIndex],
        sets: updatedSets,
      };
    }
    setRoutineExercises(newRoutineExercises);
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      title={routine ? "Edit Routine" : "Create New Routine"}
    >
      <Dialog.Content>
        <p>
          Use workout routines to organize exercises for different training days
          or goals.
        </p>
        <TextInput
          label="Routine Name"
          value={routineName || ""}
          onChange={(e) => setRoutineName(e.target.value)}
          errorText={validationErrors.name}
          placeholder="e.g., Upper Body Strength"
        />
        <p>
          <label className="block text-sm font-medium text-gray-700">
            Exercises
          </label>
          <span className="text-sm">
            The routine exercises can be edited after saving.{" "}
            <button
              onClick={() => openDialog("create-exercise")}
              className=" text-primary underline"
            >
              Need to create a new exercise?
            </button>
          </span>
        </p>
        {/* Exercises Section */}
        <RoutineExerciseList
          resourceId={routine?.id ?? ""}
          routineExercises={routineExercises}
          availableExercises={availableExercises}
          onExerciseChange={handleRoutineExerciseChange}
          onDeleteExercise={handleDeleteRoutineExercise}
          onAddExercise={handleAddRoutineExercise}
          onAddSet={handleAddSet}
          onEditSet={handleEditSet}
          isEditable={true}
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          variant="submit"
          onClick={handleSave}
          disabled={
            createRoutineMutation.isPending || patchRoutineMutation.isPending
          }
        >
          Save
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export { RoutineDialog };
